import type { Tone, TranscriptLine } from '../../ui/v2';

export const ADMIN_CALLS_PAGE_SIZE = 20;

export interface AdminCallsPage {
  calls: any[];
  /** Absent when the body is not a list: the previous count stays. */
  count?: number;
  /** Absent when the body is not a list: the previous page total stays. */
  totalPages?: number;
}

export const readAdminCallsPage = (data: any): AdminCallsPage => {
  if (data?.results && Array.isArray(data.results)) {
    return {
      calls: data.results,
      count: data.count || 0,
      totalPages:
        data.total_pages ||
        Math.ceil((data.count || 0) / ADMIN_CALLS_PAGE_SIZE),
    };
  }
  if (Array.isArray(data)) {
    return { calls: data, count: data.length, totalPages: 1 };
  }
  return { calls: [] };
};

/**
 * `m:ss` from `duration_ms`, else from start/end; a zero `duration_ms` falls
 * through to start/end.
 */
export const formatAdminCallDuration = (call: any): string => {
  const durationSec = call.duration_ms
    ? Math.floor(call.duration_ms / 1000)
    : call.start_time && call.end_time
      ? Math.max(
          0,
          Math.floor(
            (new Date(call.end_time).getTime() -
              new Date(call.start_time).getTime()) /
              1000,
          ),
        )
      : null;
  return durationSec != null
    ? `${Math.floor(durationSec / 60)}:${(durationSec % 60).toString().padStart(2, '0')}`
    : '—';
};

/** `call_status` with underscores as spaces and each word capitalised. */
export const adminCallStatusLabel = (status?: string | null) =>
  status
    ? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

export const adminCallStatusTone = (status?: string | null): Tone => {
  const s = (status || '').toLowerCase();
  if (s.includes('end') || s.includes('complete') || s.includes('success')) {
    return 'success';
  }
  if (s.includes('fail') || s.includes('busy') || s.includes('no-answer')) {
    return 'danger';
  }
  if (s.includes('progress') || s.includes('ring')) return 'warning';
  return 'neutral';
};

/** First letter upper-cased, the rest as sent; null when there is no direction. */
export const adminCallDirectionLabel = (direction?: string | null) =>
  direction ? direction.charAt(0).toUpperCase() + direction.slice(1) : null;

/** Inbound reads as accent; outbound and anything else stay neutral. */
export const adminCallDirectionTone = (
  direction?: string | null,
): 'neutral' | 'accent' =>
  (direction || '').toLowerCase().includes('in') ? 'accent' : 'neutral';

export const adminCallSentimentTone = (sentiment?: string | null): Tone => {
  const s = (sentiment || '').toLowerCase();
  if (s === 'positive') return 'success';
  if (s === 'negative') return 'danger';
  return 'neutral';
};

/** The row's cost link treats `combined_cost` as cents. */
export const formatAdminCallCost = (combinedCost: number) =>
  `$${(combinedCost / 100).toFixed(2)}`;

export const formatAdminCallCreatedAt = (createdAt?: string | null) =>
  createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Display dollar amounts from the cost API as whole cents (integers). */
export function dollarsToCentsInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return String(Math.round(n * 100));
}

export const formatCostDuration = (seconds?: number | null) =>
  seconds != null ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : '—';

export const adminCallProductLabel = (product?: string | null) =>
  (product || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Blank lines dropped; "Agent:", "User:" or "Customer:" (any case, at the
 * very start of the line) marks a speaker turn and every other line is a
 * note. Turns are not merged.
 */
export const parseAdminCallTranscript = (
  transcript?: string | null,
): TranscriptLine[] =>
  (transcript || '')
    .split('\n')
    .filter((line) => line.trim())
    .map((line): TranscriptLine => {
      const match = line.match(/^(Agent|User|Customer):\s*(.*)$/i);
      if (!match) return { speaker: 'note', text: line };
      return {
        speaker: match[1].toLowerCase() === 'agent' ? 'arthur' : 'guest',
        text: match[2],
      };
    });
